import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Modal, Fade, LinearProgress, IconButton, 
  TextField, Switch, CircularProgress 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QrCode from '@mui/icons-material/QrCode';
import Email from '@mui/icons-material/Email';
import VpnKey from '@mui/icons-material/VpnKey';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { useMFA } from '../contexts/MFAContext';
import { MFATypes } from '@flashbacktech/flashbackclient/api';
import MagicLinkSetup from './mfa/MagicLinkSetup';
import PasskeySetup from './mfa/PasskeySetup';

// Tutorial step icons
const WelcomeIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
  </svg>
);

const ProfileIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const NotificationIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 01-3.46 0"/>
  </svg>
);

const TwoFAIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0110 0v4"/>
    <circle cx="12" cy="16" r="1" fill="#617BFF"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
    <path d="M20 6L9 17l-5-5"/>
  </svg>
);

interface AccountSettingsTutorialProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  onNavigate: (section: 'profile' | 'security' | 'notifications' | '2fa') => void;
  initialStep?: number;
}

const TOTAL_STEPS = 5; // Welcome, Profile, Notifications, 2FA, Complete

// Check if 2FA was skipped previously (for reminder logic)
const was2FASkipped = () => {
  try {
    return localStorage.getItem('2faSkippedInTutorial') === 'true';
  } catch {
    return false;
  }
};

const mark2FASkipped = () => {
  try {
    localStorage.setItem('2faSkippedInTutorial', 'true');
  } catch { /* ignore */ }
};

const clear2FASkipped = () => {
  try {
    localStorage.removeItem('2faSkippedInTutorial');
  } catch { /* ignore */ }
};

const AccountSettingsTutorial: React.FC<AccountSettingsTutorialProps> = ({
  open,
  onClose,
  onComplete,
  initialStep,
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep ?? 0);

  // Sync currentStep when initialStep or open changes
  useEffect(() => {
    if (open && initialStep !== undefined) {
      setCurrentStep(initialStep);
    } else if (open && initialStep === undefined) {
      setCurrentStep(0);
    }
  }, [open, initialStep]);
  const { globalData, authState, refreshGlobalData } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  
  // Profile fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Notification fields
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [pushNotifications, setPushNotifications] = useState(false);
  
  // 2FA fields
  type MfaSubStep = 'choose' | 'GOOGLE_AUTH' | 'MAGIC_LINK' | 'PASSKEY';
  const [mfaSubStep, setMfaSubStep] = useState<MfaSubStep>('choose');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaQrCode, setMfaQrCode] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const { clearError: clearMfaError, mfaState: mfaContextState, fetchAvailableMethods, fetchMFAStatus } = useMFA();

  const userName = globalData?.userProfile?.firstName || 'there';
  const userEmail = globalData?.userProfile?.email || authState?.user?.email || '';
  const progress = ((currentStep + 1) / TOTAL_STEPS) * 100;
  const isLastStep = currentStep === TOTAL_STEPS - 1;
  const isFirstStep = currentStep === 0;

  // Pre-fill fields with existing user data
  useEffect(() => {
    if (globalData?.userProfile) {
      setFirstName(globalData.userProfile.firstName || '');
      setLastName(globalData.userProfile.lastName || '');
    }
  }, [globalData?.userProfile]);

  // Load notification settings
  useEffect(() => {
    const loadSettings = async () => {
      if (!authState?.token) return;
      try {
        const data = await withTokenRefresh(() => apiClient.getUserSettings());
        if (data?.success && data.settings?.notifications) {
          setEmailNotifications(data.settings.notifications.email || false);
          setPushNotifications(data.settings.notifications.push || false);
        }
      } catch { /* ignore */ }
    };
    if (open && currentStep === 2) {
      loadSettings();
    }
  }, [open, currentStep, authState?.token, withTokenRefresh, apiClient]);

  // Consider 2FA "fully done" only when all 3 methods are configured (so we show picker until then)
  useEffect(() => {
    const methods = mfaContextState?.methods ?? [];
    const allThreeConfigured = [
      MFATypes.MFAType.GOOGLE_AUTH,
      MFATypes.MFAType.MAGIC_LINK,
      MFATypes.MFAType.PASSKEY,
    ].every((type) => methods.some((m) => m.type === type && m.isConfigured));
    setMfaEnabled(allThreeConfigured);
  }, [mfaContextState?.methods]);

  // Reset MFA sub-step when leaving step 3
  useEffect(() => {
    if (currentStep !== 3) {
      setMfaSubStep('choose');
    }
  }, [currentStep]);

  // Fetch MFA methods and status when entering step 3 (configured list + primary method)
  useEffect(() => {
    if (open && currentStep === 3) {
      fetchMFAStatus?.();
      fetchAvailableMethods?.();
    }
  }, [open, currentStep, fetchAvailableMethods, fetchMFAStatus]);

  // Generate Authenticator (Google Auth) setup only when user chose that method
  useEffect(() => {
    const setup2FA = async () => {
      if (!authState?.token || mfaEnabled || mfaSecret) return;
      setMfaLoading(true);
      try {
        const res = await withTokenRefresh(() =>
          apiClient.setupMFA({ mfaType: MFATypes.MFAType.GOOGLE_AUTH })
        );
        const data = res?.data as { secret?: string; qrCode?: string } | undefined;
        if (data?.secret) {
          setMfaSecret(data.secret);
          if (data.qrCode) {
            setMfaQrCode(data.qrCode);
          }
        }
      } catch { /* ignore */ }
      setMfaLoading(false);
    };
    if (open && currentStep === 3 && !mfaEnabled && mfaSubStep === 'GOOGLE_AUTH') {
      setup2FA();
    }
  }, [open, currentStep, authState?.token, mfaEnabled, mfaSubStep, withTokenRefresh, apiClient]);

  // Save profile
  const saveProfile = async () => {
    if (!firstName.trim() || !lastName.trim()) return false;
    setSaving(true);
    setError(null);
    try {
      const res = await withTokenRefresh(() => 
        apiClient.updateUser(globalData?.userProfile?.id || '', { name: firstName.trim(), lastName: lastName.trim() })
      );
      setSaving(false);
      await refreshGlobalData(true); // Force refresh to bypass cache, so new name/lastName shows everywhere
      return res?.success;
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
      setSaving(false);
      return false;
    }
  };

  // Save notifications
  const saveNotifications = async () => {
    setSaving(true);
    try {
      await withTokenRefresh(() => 
        apiClient.partialUpdateUserSettings({ 
          notifications: { email: emailNotifications, push: pushNotifications } 
        })
      );
      setSaving(false);
      return true;
    } catch {
      setSaving(false);
      return true; // Continue anyway
    }
  };

  // Verify 2FA (Authenticator app)
  const verify2FA = async () => {
    if (!mfaCode || mfaCode.length !== 6) return false;
    setMfaLoading(true);
    try {
      const res = await withTokenRefresh(() =>
        apiClient.verifyMFASetup({ mfaType: MFATypes.MFAType.GOOGLE_AUTH, code: mfaCode })
      );
      setMfaLoading(false);
      if (res?.success) {
        setMfaEnabled(true);
        return true;
      }
      setError('Invalid code. Please try again.');
      return false;
    } catch (e: any) {
      setError(e?.message || 'Failed to verify code');
      setMfaLoading(false);
      return false;
    }
  };

  const handleNext = async () => {
    setError(null);
    
    if (currentStep === 1) {
      // Profile step - save profile
      const success = await saveProfile();
      if (!success) return;
    } else if (currentStep === 2) {
      // Notifications step - save notifications
      await saveNotifications();
    } else if (currentStep === 3 && !mfaEnabled && mfaCode) {
      // 2FA step - verify if code entered
      const success = await verify2FA();
      if (success) {
        // Clear the skip flag if 2FA was successfully enabled
        clear2FASkipped();
      }
      if (!success) return;
    } else if (currentStep === 3 && !mfaEnabled && !mfaCode) {
      // 2FA step skipped - mark it for reminder
      mark2FASkipped();
    }
    
    if (isLastStep) {
      onComplete();
      try {
        localStorage.setItem('accountSettingsTutorialCompleted', 'true');
      } catch { void 0; }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setError(null);
      if (currentStep === 3 && mfaSubStep !== 'choose') {
        setMfaSubStep('choose');
        setMfaSecret('');
        setMfaQrCode('');
        setMfaCode('');
        clearMfaError();
      } else {
        setCurrentStep(prev => prev - 1);
      }
    }
  };

  const handleSkip = () => {
    // If skipping during 2FA step, mark it for reminder
    if (currentStep === 3 && !mfaEnabled) {
      mark2FASkipped();
    }
    onClose();
    try {
      localStorage.setItem('accountSettingsTutorialCompleted', 'true');
    } catch { void 0; }
  };

  // Check if Next button should be enabled
  const isNextEnabled = () => {
    if (currentStep === 1) {
      return firstName.trim().length > 0 && lastName.trim().length > 0;
    }
    if (currentStep === 3 && !mfaEnabled) {
      return mfaCode.length === 6 || true; // Allow skipping 2FA
    }
    return true;
  };

  // Get step title
  const getStepTitle = () => {
    switch (currentStep) {
      case 0: return `Hey ${userName}!`;
      case 1: return 'Personal Information';
      case 2: return 'Notifications';
      case 3: return 'Two-Factor Authentication';
      case 4: return "You're All Set!";
      default: return '';
    }
  };

  // Get step icon
  const getStepIcon = () => {
    switch (currentStep) {
      case 0: return <WelcomeIcon />;
      case 1: return <ProfileIcon />;
      case 2: return <NotificationIcon />;
      case 3: return <TwoFAIcon />;
      case 4: return (
        <Box sx={{ 
          width: 48, height: 48, borderRadius: '50%', bgcolor: '#10B981',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CheckIcon />
        </Box>
      );
      default: return <WelcomeIcon />;
    }
  };

  // Get step gradient
  const getStepGradient = () => {
    switch (currentStep) {
      case 0: return 'linear-gradient(135deg, #617BFF 0%, #8B5CF6 100%)';
      case 1: return 'linear-gradient(135deg, #617BFF 0%, #818CF8 100%)';
      case 2: return 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)';
      case 3: return 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)';
      case 4: return 'linear-gradient(135deg, #10B981 0%, #34D399 100%)';
      default: return 'linear-gradient(135deg, #617BFF 0%, #8B5CF6 100%)';
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Welcome
        return (
          <Typography sx={{ fontSize: '15px', color: '#535862', lineHeight: 1.7, textAlign: 'center' }}>
            Welcome to Flashgate, let's configure your account to enjoy the full experience of Flashgate.
          </Typography>
        );
      
      case 1: // Profile
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 0.5 }}>
                First Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    bgcolor: '#F9FAFB',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 0.5 }}>
                Last Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter your last name"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    bgcolor: '#F9FAFB',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 0.5 }}>
                Email
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={userEmail}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    bgcolor: '#F3F4F6',
                  },
                }}
              />
            </Box>
          </Box>
        );
      
      case 2: // Notifications
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ fontSize: '14px', color: '#535862', textAlign: 'center', mb: 1 }}>
              Choose how you'd like to receive notifications.
            </Typography>
            <Box sx={{ 
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              p: 2, bgcolor: '#F9FAFB', borderRadius: '12px', border: '1px solid #E5E7EB',
            }}>
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#344054' }}>
                  Email Notifications
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#667085' }}>
                  Receive updates via email
                </Typography>
              </Box>
              <Switch
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#617BFF' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#617BFF' },
                }}
              />
            </Box>
            <Box sx={{ 
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              p: 2, bgcolor: '#F9FAFB', borderRadius: '12px', border: '1px solid #E5E7EB',
            }}>
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#344054' }}>
                  Push Notifications
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#667085' }}>
                  Receive push notifications
                </Typography>
              </Box>
              <Switch
                checked={pushNotifications}
                onChange={(e) => setPushNotifications(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#617BFF' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#617BFF' },
                }}
              />
            </Box>
          </Box>
        );
      
      case 3: // 2FA
        // Only show "all done" when all 3 MFA methods are configured; otherwise show picker to add more
        if (mfaEnabled) {
          return (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{
                width: 64, height: 64, borderRadius: '50%', bgcolor: '#D1FAE5',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2,
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
              </Box>
              <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#10B981', mb: 1 }}>
                2FA Already Enabled
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                Your account is already protected with two-factor authentication.
              </Typography>
            </Box>
          );
        }

        if (mfaSubStep === 'choose') {
          const methods = mfaContextState?.methods ?? [];
          const primaryMethod = mfaContextState?.status?.primaryMethod;
          const isAuthenticatorConfigured = methods.some((m) => m.type === MFATypes.MFAType.GOOGLE_AUTH && m.isConfigured && m.isEnabled);
          const isMagicLinkConfigured = methods.some((m) => m.type === MFATypes.MFAType.MAGIC_LINK && m.isConfigured && m.isEnabled);
          const isPasskeyConfigured = methods.some((m) => m.type === MFATypes.MFAType.PASSKEY && m.isConfigured && m.isEnabled);

          const renderMethodLabel = (configured: boolean, isPrimary: boolean) => {
            if (configured && isPrimary) return 'Default · Already configured';
            if (configured) return 'Already configured';
            return null;
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography sx={{ fontSize: '14px', color: '#535862', textAlign: 'center', mb: 1 }}>
                Add or manage two-factor authentication methods. You can configure more than one.
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                disabled={isAuthenticatorConfigured}
                onClick={() => !isAuthenticatorConfigured && setMfaSubStep('GOOGLE_AUTH')}
                sx={{
                  justifyContent: 'flex-start',
                  py: 1.5,
                  px: 2,
                  borderRadius: '12px',
                  borderColor: isAuthenticatorConfigured ? '#E5E7EB' : '#E5E7EB',
                  color: isAuthenticatorConfigured ? '#9CA3AF' : '#344054',
                  textTransform: 'none',
                  opacity: isAuthenticatorConfigured ? 0.8 : 1,
                  '&:hover': !isAuthenticatorConfigured ? { borderColor: '#617BFF', bgcolor: 'rgba(97, 123, 255, 0.04)' } : {},
                  '&.Mui-disabled': { color: '#9CA3AF', borderColor: '#E5E7EB' },
                }}
              >
                <QrCode sx={{ mr: 2, color: isAuthenticatorConfigured ? '#9CA3AF' : '#617BFF' }} />
                <Box sx={{ textAlign: 'left', flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                    Authenticator app
                    {isAuthenticatorConfigured && primaryMethod === MFATypes.MFAType.GOOGLE_AUTH && (
                      <Typography component="span" sx={{ ml: 1, fontSize: '12px', fontWeight: 500, color: '#10B981' }}>
                        (Default)
                      </Typography>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#667085' }}>
                    {renderMethodLabel(isAuthenticatorConfigured, primaryMethod === MFATypes.MFAType.GOOGLE_AUTH)
                      ?? 'Use Google/MS Authenticator, Authy, etc.'}
                  </Typography>
                </Box>
              </Button>
              <Button
                fullWidth
                variant="outlined"
                disabled={isMagicLinkConfigured}
                onClick={() => !isMagicLinkConfigured && setMfaSubStep('MAGIC_LINK')}
                sx={{
                  justifyContent: 'flex-start',
                  py: 1.5,
                  px: 2,
                  borderRadius: '12px',
                  borderColor: '#E5E7EB',
                  color: isMagicLinkConfigured ? '#9CA3AF' : '#344054',
                  textTransform: 'none',
                  opacity: isMagicLinkConfigured ? 0.8 : 1,
                  '&:hover': !isMagicLinkConfigured ? { borderColor: '#617BFF', bgcolor: 'rgba(97, 123, 255, 0.04)' } : {},
                  '&.Mui-disabled': { color: '#9CA3AF', borderColor: '#E5E7EB' },
                }}
              >
                <Email sx={{ mr: 2, color: isMagicLinkConfigured ? '#9CA3AF' : '#617BFF' }} />
                <Box sx={{ textAlign: 'left', flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                    Magic Link
                    {isMagicLinkConfigured && primaryMethod === MFATypes.MFAType.MAGIC_LINK && (
                      <Typography component="span" sx={{ ml: 1, fontSize: '12px', fontWeight: 500, color: '#10B981' }}>
                        (Default)
                      </Typography>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#667085' }}>
                    {renderMethodLabel(isMagicLinkConfigured, primaryMethod === MFATypes.MFAType.MAGIC_LINK)
                      ?? 'Receive a one-time link by email'}
                  </Typography>
                </Box>
              </Button>
              <Button
                fullWidth
                variant="outlined"
                disabled={isPasskeyConfigured}
                onClick={() => !isPasskeyConfigured && setMfaSubStep('PASSKEY')}
                sx={{
                  justifyContent: 'flex-start',
                  py: 1.5,
                  px: 2,
                  borderRadius: '12px',
                  borderColor: '#E5E7EB',
                  color: isPasskeyConfigured ? '#9CA3AF' : '#344054',
                  textTransform: 'none',
                  opacity: isPasskeyConfigured ? 0.8 : 1,
                  '&:hover': !isPasskeyConfigured ? { borderColor: '#617BFF', bgcolor: 'rgba(97, 123, 255, 0.04)' } : {},
                  '&.Mui-disabled': { color: '#9CA3AF', borderColor: '#E5E7EB' },
                }}
              >
                <VpnKey sx={{ mr: 2, color: isPasskeyConfigured ? '#9CA3AF' : '#617BFF' }} />
                <Box sx={{ textAlign: 'left', flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                    Passkey
                    {isPasskeyConfigured && primaryMethod === MFATypes.MFAType.PASSKEY && (
                      <Typography component="span" sx={{ ml: 1, fontSize: '12px', fontWeight: 500, color: '#10B981' }}>
                        (Default)
                      </Typography>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#667085' }}>
                    {renderMethodLabel(isPasskeyConfigured, primaryMethod === MFATypes.MFAType.PASSKEY)
                      ?? 'Biometrics or device PIN'}
                  </Typography>
                </Box>
              </Button>
              <Typography sx={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center' }}>
                You can skip this step and manage 2FA later in your security settings.
              </Typography>
              {(isAuthenticatorConfigured || isMagicLinkConfigured || isPasskeyConfigured) && (
                <Typography sx={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center', mt: 0.5 }}>
                  In Profile → Security you can change which method is default or disable a method.
                </Typography>
              )}
            </Box>
          );
        }

        if (mfaSubStep === 'MAGIC_LINK') {
          return (
            <Box sx={{ minHeight: 200 }}>
              <MagicLinkSetup
                onSetupComplete={() => {
                  setMfaEnabled(true);
                  clear2FASkipped();
                }}
              />
            </Box>
          );
        }

        if (mfaSubStep === 'PASSKEY') {
          return (
            <Box sx={{ minHeight: 200 }}>
              <PasskeySetup
                onSetupComplete={() => {
                  setMfaEnabled(true);
                  clear2FASkipped();
                }}
              />
            </Box>
          );
        }

        // GOOGLE_AUTH: Authenticator app flow
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: '14px', color: '#535862', textAlign: 'center' }}>
              Scan this QR code with your authenticator app (Google/MS Authenticator, Authy, etc.)
            </Typography>
            
            {mfaLoading ? (
              <Box sx={{ py: 4 }}>
                <CircularProgress size={40} sx={{ color: '#617BFF' }} />
              </Box>
            ) : mfaQrCode ? (
              <>
                <Box sx={{ 
                  p: 2, bgcolor: 'white', borderRadius: '12px', 
                  border: '1px solid #E5E7EB', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                  <img src={mfaQrCode} alt="2FA QR Code" style={{ width: 160, height: 160 }} />
                </Box>
                
                <Typography sx={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center' }}>
                  Or enter this code manually: <strong style={{ color: '#414651' }}>{mfaSecret}</strong>
                </Typography>
                
                <Box sx={{ width: '100%', mt: 1 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 0.5 }}>
                    Enter verification code
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    inputProps={{ maxLength: 6, style: { textAlign: 'center', letterSpacing: '8px', fontSize: '18px' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        bgcolor: '#F9FAFB',
                      },
                    }}
                  />
                </Box>
              </>
            ) : mfaSecret ? (
              <>
                <Typography sx={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center' }}>
                  Enter this code manually in your app: <strong style={{ color: '#414651' }}>{mfaSecret}</strong>
                </Typography>
                <Box sx={{ width: '100%', mt: 1 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 0.5 }}>
                    Enter verification code
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    inputProps={{ maxLength: 6, style: { textAlign: 'center', letterSpacing: '8px', fontSize: '18px' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        bgcolor: '#F9FAFB',
                      },
                    }}
                  />
                </Box>
              </>
            ) : (
              <Typography sx={{ fontSize: '14px', color: '#F04438' }}>
                Failed to load setup. You can set up 2FA later in settings.
              </Typography>
            )}
            
            <Typography sx={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center' }}>
              You can skip this step and set up 2FA later in your security settings.
            </Typography>
          </Box>
        );
      
      case 4: // Complete
        return (
          <Typography sx={{ fontSize: '15px', color: '#535862', lineHeight: 1.7, textAlign: 'center' }}>
            Great job! Your account is now configured. You can access these settings anytime from your profile menu.
          </Typography>
        );
      
      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleSkip}
      closeAfterTransition
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        },
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            maxWidth: 480,
            width: '90%',
            outline: 'none',
            overflow: 'hidden',
          }}
        >
          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 4,
              bgcolor: '#E5E7EB',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#617BFF',
              },
            }}
          />

          {/* Header */}
          <Box
            sx={{
              background: getStepGradient(),
              p: 3,
              textAlign: 'center',
              position: 'relative',
            }}
          >
            {/* Close button */}
            <IconButton
              onClick={handleSkip}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'rgba(255,255,255,0.8)',
                '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Step indicator */}
            <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', mb: 1.5, fontWeight: 500 }}>
              Step {currentStep + 1} of {TOTAL_STEPS}
            </Typography>

            {/* Icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1.5,
                boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
              }}
            >
              {getStepIcon()}
            </Box>

            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'white' }}>
              {getStepTitle()}
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3 }}>
            {renderStepContent()}

            {/* Error message */}
            {error && (
              <Typography sx={{ fontSize: '13px', color: '#F04438', textAlign: 'center', mt: 2 }}>
                {error}
              </Typography>
            )}

            {/* Navigation dots */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, my: 3 }}>
              {Array.from({ length: TOTAL_STEPS }).map((_, idx) => (
                <Box
                  key={idx}
                  sx={{
                    width: idx === currentStep ? 24 : 8,
                    height: 8,
                    borderRadius: '4px',
                    bgcolor: idx === currentStep ? '#617BFF' : idx < currentStep ? '#10B981' : '#E5E7EB',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>

            {/* Navigation buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {!isFirstStep && (
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={saving || mfaLoading}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    borderRadius: '12px',
                    borderColor: '#E5E7EB',
                    color: '#667085',
                    fontWeight: 600,
                    fontSize: '15px',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#D1D5DB',
                      bgcolor: '#F9FAFB',
                    },
                  }}
                >
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isNextEnabled() || saving || mfaLoading}
                sx={{
                  flex: 1,
                  py: 1.5,
                  borderRadius: '12px',
                  bgcolor: isNextEnabled() ? (isLastStep ? '#10B981' : '#617BFF') : '#E5E7EB',
                  color: isNextEnabled() ? 'white' : '#9CA3AF',
                  fontWeight: 600,
                  fontSize: '15px',
                  textTransform: 'none',
                  boxShadow: isNextEnabled() 
                    ? (isLastStep ? '0 4px 14px rgba(16, 185, 129, 0.4)' : '0 4px 14px rgba(97, 123, 255, 0.4)')
                    : 'none',
                  '&:hover': {
                    bgcolor: isNextEnabled() ? (isLastStep ? '#059669' : '#5A6FE8') : '#E5E7EB',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#E5E7EB',
                    color: '#9CA3AF',
                  },
                }}
              >
                {saving || mfaLoading ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : isLastStep ? (
                  'Complete Setup'
                ) : currentStep === 3 && !mfaEnabled && (mfaSubStep === 'choose' || (mfaSubStep === 'GOOGLE_AUTH' && !mfaCode)) ? (
                  'Skip for now'
                ) : (
                  'Next'
                )}
              </Button>
            </Box>

            {/* Skip link */}
            {!isLastStep && (
              <Button
                variant="text"
                fullWidth
                onClick={handleSkip}
                sx={{
                  mt: 1.5,
                  color: '#9CA3AF',
                  fontSize: '13px',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: '#667085',
                  },
                }}
              >
                Skip tutorial
              </Button>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default AccountSettingsTutorial;
